// frontend/src/livequiz/LiveQuizStudentPoll.js
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './livequiz.css';
import { useLiveQuizConnection, joinLiveQuizSession } from './useLiveQuizConnection';
import AnimatedNumber from './AnimatedNumber';
import Confetti from './Confetti';
import { playQuestionSound, playCorrectSound, playWrongSound } from './sound';
import CountdownTimer from './CountdownTimer';


const LiveQuizStudentPoll = ({ studentId, initialSessionCode }) => {
    const navigate = useNavigate();
    const leaveQuiz = () => {
        if (window.confirm('Leave this quiz? Your score is saved — you can rejoin with the same code.')) {
            navigate('/dashboard');
        }
    };
    const [sessionCode, setSessionCode] = useState(initialSessionCode || '');
    const [status, setStatus] = useState(initialSessionCode ? 'joining' : 'join'); // joining / waiting / active / completed
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [answerResult, setAnswerResult] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [error, setError] = useState(null);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [soundOn, setSoundOn] = useState(localStorage.getItem('lqSoundOff') !== '1');
    const [timeUp, setTimeUp] = useState(false);

    const handleMessage = useCallback((data) => {
        switch (data.event) {
            case 'quiz_started':
            case 'next_question':
                setStatus('active');
                setCurrentQuestion(data.question);
                if (localStorage.getItem('lqSoundOff') !== '1') playQuestionSound();
                setSelectedAnswer(null);
                setAnswerResult(null);
                setTimeUp(false);
                break;

            case 'answer_result':
                if (localStorage.getItem('lqSoundOff') !== '1') {
                    if (data.is_correct) playCorrectSound(); else playWrongSound();
                }
                setAnswerResult(data);
                setHistory(prev => [...prev, {
                    question: currentQuestion,
                    selected: selectedAnswer,
                    is_correct: data.is_correct,
                    correct_answer: data.correct_answer
                }]);
                break;

            case 'scorecard':
                setLeaderboard(data.leaderboard || []);
                break;

            case 'quiz_ended':
                setStatus('completed');
                setLeaderboard(data.leaderboard || []);
                break;

            case 'sync_state':
                if (data.status === 'active' && data.question) {
                    setStatus('active');
                    setCurrentQuestion(data.question);
                    if (data.current_question_answer) {
                        setSelectedAnswer(data.current_question_answer.selected);
                        setAnswerResult({
                            is_correct: data.current_question_answer.is_correct,
                            correct_answer: data.current_question_answer.correct_answer,
                            points_awarded: data.current_question_answer.points_awarded
                        });
                    } else {
                        setSelectedAnswer(null);
                        setAnswerResult(null);
                    }
                    if (data.history) setHistory(data.history);
                } else if (data.status === 'completed') {
                    setStatus('completed');
                    setLeaderboard(data.leaderboard || []);
                } else if (data.status === 'waiting') {
                    setStatus('waiting');
                }
                break;

            case 'time_up':
                setTimeUp(true);
                break;

            case 'question_closed':
                if (data.answered === false && currentQuestion &&
                    currentQuestion.question_id === data.question_id) {
                    setHistory(prev => [...prev, {
                        question: currentQuestion,
                        selected: null,
                        is_correct: false,
                        correct_answer: null,
                        skipped: true
                    }]);
                }
                break;

            default:
                break;
        }
    }, [currentQuestion, selectedAnswer]);

    const { connect, send, connectionError } = useLiveQuizConnection(studentId, handleMessage);

    const joinSession = useCallback(async (codeToUse) => {
        const code = codeToUse || sessionCode;
        if (!code.trim()) {
            setError('Please enter a session code');
            return;
        }
        setError(null);
        try {
            await joinLiveQuizSession(code, studentId);
            setStatus('waiting');
            connect(code.toUpperCase());
        } catch (err) {
            setError(err.message);
        }
    }, [sessionCode, studentId, connect]);

    useEffect(() => {
        if (initialSessionCode) {
            joinSession(initialSessionCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSessionCode]);

    const submitAnswer = (option) => {
        if (answerResult || timeUp || !currentQuestion) return;
        setSelectedAnswer(option);
        send({
            action: 'submit_answer',
            question_id: currentQuestion.question_id,
            selected_answer: option
        });
    };

    const myRank = leaderboard.findIndex(e => e.student_id === studentId) + 1;
    const myScore = leaderboard.find(e => e.student_id === studentId)?.total_score ?? 0;

    return (
        <div className="livequiz-container">
            <h2 className="livequiz-title">Live Quiz — Poll</h2>

            {(error || connectionError) && <div className="livequiz-error">{error || connectionError}</div>}

            {status === 'join' && (
                <div className="livequiz-setup-card">
                    <label className="livequiz-label">Enter Session Code</label>
                    <input
                        type="text"
                        className="livequiz-input livequiz-code-input"
                        value={sessionCode}
                        onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
                        placeholder="e.g. AB12CD"
                        maxLength={10}
                    />
                    <button className="livequiz-primary-btn" onClick={joinSession}>
                        Join Quiz
                    </button>
                </div>
            )}

            {status === 'waiting' && (
                <div className="livequiz-waiting-card">
                    <button className="livequiz-secondary-btn"
                        style={{ flex: 'none', padding: '6px 12px', minHeight: 'auto', marginBottom: '10px' }}
                        onClick={leaveQuiz}>
                        ← Leave
                    </button>
                    <div className="livequiz-waiting-message">
                        <div className="livequiz-spinner"></div>
                        <p>You're in! Waiting for teacher to start the quiz...</p>
                    </div>
                </div>
            )}

            {status === 'active' && currentQuestion && (
                <div className="livequiz-active-card">
                    <div className="livequiz-progress-bar-wrap">
                        <span>Question {(currentQuestion.index ?? 0) + 1} of {currentQuestion.total}</span>
                    </div>
                    <div className="livequiz-progress-dots">
                        {Array.from({ length: currentQuestion.total }, (_, i) => (
                            <div
                                key={i}
                                className={`livequiz-progress-dot ${i < currentQuestion.index ? 'completed' : ''} ${i === currentQuestion.index ? 'current' : ''}`}
                            />
                        ))}
                    </div>

                    <button className="livequiz-secondary-btn"
                        style={{ flex: 'none', padding: '6px 12px', minHeight: 'auto', marginBottom: '10px', marginRight: '8px' }}
                        onClick={leaveQuiz}>
                        ← Leave
                    </button>
                    <button
                        className="livequiz-secondary-btn"
                        style={{ flex: 'none', padding: '6px 12px', minHeight: 'auto', marginBottom: '10px' }}
                        onClick={() => {
                            const next = !soundOn;
                            setSoundOn(next);
                            localStorage.setItem('lqSoundOff', next ? '0' : '1');
                        }}
                    >
                        {soundOn ? '🔊' : '🔇'}
                    </button>

                    <CountdownTimer
                        seconds={currentQuestion.time_per_question}
                        resetKey={currentQuestion.question_id}
                    />

                    <div className="livequiz-question-display">
                        {(currentQuestion.chapter_name || currentQuestion.topic_name) && (
                            <p className="livequiz-question-context">
                                {currentQuestion.chapter_name}
                                {currentQuestion.topic_name ? ` · ${currentQuestion.topic_name}` : ''}
                            </p>
                        )}
                        <p className="livequiz-question-text">{currentQuestion.text}</p>
                    </div>

                    <div className="livequiz-answer-options">
                        {['A', 'B', 'C', 'D'].map(opt => {
                            const optionText = currentQuestion[`option${opt}`];
                            if (!optionText) return null;
                            const isSelected = selectedAnswer === opt;
                            const isCorrect = answerResult && answerResult.correct_answer === opt;
                            const isWrongSelected = answerResult && isSelected && !answerResult.is_correct;

                            return (
                                <button
                                    key={opt}
                                    className={`livequiz-answer-btn 
                                        ${isSelected ? 'selected' : ''} 
                                        ${answerResult && isCorrect ? 'correct' : ''} 
                                        ${isWrongSelected ? 'wrong' : ''}`}
                                    onClick={() => submitAnswer(opt)}
                                    disabled={!!answerResult || timeUp}
                                >
                                    <span className="livequiz-option-letter">{opt}</span>
                                    <span className="livequiz-option-text">{optionText}</span>
                                </button>
                            );
                        })}
                    </div>

                    {timeUp && !answerResult && (
                        <div className="livequiz-result-banner wrong">⏱ Time's up — answer locked.</div>
                    )}

                    {answerResult && (
                        <div className={`livequiz-result-banner ${answerResult.is_correct ? 'correct' : 'wrong'}`}>
                            {answerResult.is_correct
                                ? `Correct! +${answerResult.points_awarded} points`
                                : `Incorrect. Correct answer: ${answerResult.correct_answer}${answerResult.points_awarded < 0 ? ` (${answerResult.points_awarded} points)` : ''}`}
                        </div>
                    )}

                    {history.length > 0 && (
                        <button className="livequiz-secondary-btn" onClick={() => setShowHistory(!showHistory)} style={{ marginBottom: '10px' }}>
                            {showHistory ? 'Hide' : 'View'} Previous Questions ({history.length})
                        </button>
                    )}

                    {showHistory && (
                        <div className="livequiz-history-list">
                            {history.map((h, idx) => (
                                <div key={idx} className={`livequiz-history-item ${h.is_correct ? 'correct' : 'wrong'}`}>
                                    <p className="livequiz-history-question">{h.question?.text}</p>
                                    <p className="livequiz-history-answer">
                                        {h.skipped
                                            ? 'Not answered'
                                            : <>Your answer: {h.selected || '—'} {h.is_correct ? '✓' : `✗ (Correct: ${h.correct_answer})`}</>}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    {leaderboard.length > 0 && (
                        <div className="livequiz-leaderboard">
                            <h3>Leaderboard</h3>
                            {leaderboard.map((entry, idx) => (
                                <div
                                    key={entry.student_id}
                                    className={`livequiz-leaderboard-row ${entry.student_id === studentId ? 'me' : ''} ${idx === 0 ? 'winner' : ''}`}
                                >
                                    <span className="livequiz-rank">#{idx + 1}</span>
                                    <span className="livequiz-lb-name">{entry.name}</span>
                                    <span className="livequiz-lb-score"><AnimatedNumber value={entry.total_score} /> pts</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {status === 'completed' && (
                <div className="livequiz-completed-card">
                    {myRank === 1 && <Confetti />}
                    <h3>Quiz Completed</h3>
                    {myRank > 0 && (
                        <div className="livequiz-my-result">
                            Your Rank: #{myRank} — {myScore} points
                        </div>
                    )}
                    <div className="livequiz-leaderboard final">
                        {leaderboard.map((entry, idx) => (
                            <div
                                key={entry.student_id}
                                className={`livequiz-leaderboard-row ${entry.student_id === studentId ? 'me' : ''} ${idx === 0 ? 'winner' : ''}`}
                            >
                                <span className="livequiz-rank">#{idx + 1}</span>
                                <span className="livequiz-lb-name">{entry.name}</span>
                                <span className="livequiz-lb-score"><AnimatedNumber value={entry.total_score} /> pts</span>
                            </div>
                        ))}
                    </div>

                    {history.length > 0 && (
                        <button className="livequiz-secondary-btn" onClick={() => setShowHistory(!showHistory)} style={{ marginBottom: '10px' }}>
                            {showHistory ? 'Hide My Answers' : 'View My Answers'}
                        </button>
                    )}
                    {showHistory && (
                        <div className="livequiz-history-list">
                            {history.map((h, idx) => (
                                <div key={idx} className="livequiz-history-item">
                                    <p className="livequiz-history-question">
                                        Q{idx + 1}. {h.question?.text}
                                    </p>
                                    <p className="livequiz-history-answer">
                                        {h.skipped
                                            ? 'Not answered'
                                            : <>Your answer: {h.selected || '—'} {h.is_correct ? '✓' : `✗ (Correct: ${h.correct_answer})`}</>}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    <button className="livequiz-primary-btn" onClick={() => navigate('/dashboard')}>
                        ← Back to Dashboard
                    </button>
                </div>
            )}
        </div>
    );
};

export default LiveQuizStudentPoll;